import { Trans, useLingui } from '@lingui/react/macro'
import {
  ActionIcon,
  Anchor,
  Button,
  Card,
  FileButton,
  Group,
  Progress,
  Stack,
  Text,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { compressImage } from '@markku/components'
import {
  IconExternalLink,
  IconFile,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons-react'
import { AttachmentInfo } from '../../lib/hooks/useAttachments'

interface AttachmentFieldProps {
  attachments: AttachmentInfo[]
  isUploading: boolean
  uploadFile: (file: File) => Promise<void>
  removeAttachment: (id: number) => void
  allowedTypes?: string[] // MIME types
  disabled?: boolean
}

export function AttachmentField({
  attachments,
  isUploading,
  uploadFile,
  removeAttachment,
  allowedTypes,
  disabled,
}: AttachmentFieldProps) {
  const { t } = useLingui()
  const handleFileSelect = async (file: File | null) => {
    if (!file) return
    // Validate file type
    if (allowedTypes && !allowedTypes.includes(file.type)) {
      notifications.show({
        title: t`Invalid file type`,
        message: t`Allowed types: ${allowedTypes.join(', ')}`,
        color: 'red',
      })
      return
    }

    // Compress image if it's an image file
    const fileToUpload = await compressImage(file)
    await uploadFile(fileToUpload)
  }

  const getFileIcon = (_mimeType?: string) => {
    // You could expand this to show different icons based on file type
    return <IconFile size={16} />
  }

  return (
    <Stack gap="xs">
      {/* File list */}
      {attachments.length > 0 && (
        <Stack gap="xs">
          {attachments.map((attachment) => (
            <Card key={attachment.id} p="xs" withBorder>
              <Group justify="space-between" wrap="nowrap">
                <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                  {getFileIcon(attachment.mimeType)}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {attachment.downloadUrl ? (
                      <Anchor
                        href={attachment.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="sm"
                        truncate
                      >
                        <Group gap={4} wrap="nowrap">
                          <Text size="sm" truncate>
                            {attachment.filename}
                          </Text>
                          <IconExternalLink size={12} />
                        </Group>
                      </Anchor>
                    ) : (
                      <Text size="sm" truncate>
                        {attachment.filename}
                      </Text>
                    )}
                    <Text size="xs" c="dimmed">
                      {formatFileSize(attachment.filesize)}
                    </Text>
                  </div>
                </Group>

                {attachment.status === 'uploading' && attachment.progress ? (
                  <Progress
                    value={attachment.progress}
                    size="sm"
                    style={{ width: 60 }}
                  />
                ) : (
                  !disabled && (
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => removeAttachment(attachment.id)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  )
                )}
              </Group>

              {attachment.status === 'error' && (
                <Group gap="xs" mt="xs">
                  <IconX size={14} color="red" />
                  <Text size="xs" c="red">
                    {attachment.error}
                  </Text>
                </Group>
              )}
            </Card>
          ))}
        </Stack>
      )}

      {/* Upload button */}
      <FileButton
        onChange={handleFileSelect}
        accept={allowedTypes?.join(',')}
        disabled={disabled || isUploading}
      >
        {(props) => (
          <Button
            {...props}
            variant="light"
            leftSection={<IconUpload size={16} />}
            loading={isUploading}
            disabled={disabled}
            fullWidth={false}
          >
            <Trans>Add attachment</Trans>
          </Button>
        )}
      </FileButton>
    </Stack>
  )
}

// Helper function to format file size
function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}
